import { gql, useMutation } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import { PostPrivacy, PostType } from 'lib/generated/graphql';
import Image from 'next/image';
import { FunctionComponent, useEffect, useState } from 'react';
import useNotification from 'src/hooks/useNotification';
import { Avatar, Button } from 'src/shared-components';
import useAuth from 'src/shared-components/Auth';
import { TextArea } from 'src/shared-components/Form';
import * as Yup from 'yup';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

export const RE_POST_MUTATION = gql`
  mutation RePost($input: RepostCreateInput!, $where: PostWhereUniqueInput!) {
    repost(input: $input, where: $where) {
      id
      note
    }
  }
`;

export const CREATE_POST_MUTATION = gql`
  mutation CreatePost($input: PostCreateInput!) {
    createPost(input: $input) {
      id
      note
    }
  }
`;

interface NewPostProps {
  type: PostType;
  huddleId: number;
  projectName: string;
  optInId?: number;
  originalPostId?: number;
  onPostCreated: () => void;
  canChangePrivacy?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
}

const NewPost: FunctionComponent<NewPostProps> = ({
  type,
  huddleId,
  projectName,
  optInId,
  originalPostId,
  onPostCreated,
  canChangePrivacy = false,
  setIsLoading,
}: NewPostProps) => {
  const { user } = useAuth();
  const { showSuccess, showError, hideNotification } = useNotification();
  const [privacy, setPostPrivacy] = useState<PostPrivacy>(PostPrivacy.Public);
  const [createPostMutation] = useMutation(CREATE_POST_MUTATION);
  const [rePostMutation] = useMutation<any>(RE_POST_MUTATION);

  const form = useFormik({
    initialValues: {
      note: '',
    },
    validationSchema: Yup.object({
      note: Yup.string(),
    }),
    onSubmit: async values => {
      try {
        if (setIsLoading) {
          setIsLoading(true);
        }
        if (type === PostType.Repost) {
          await rePostMutation({
            variables: {
              where: {
                id: originalPostId,
              },
              input: {
                note: values.note,
              },
            },
          });
        } else {
          const createPostInput: any = {
            note: values.note,
            privacy,
            postType: type,
            huddle: {
              connect: {
                id: huddleId,
              },
            },
          };
          if (optInId) {
            createPostInput.optIn = {
              connect: {
                id: optInId,
              },
            };
          }
          await createPostMutation({
            variables: {
              input: createPostInput,
            },
          });
        }
        onPostCreated();
        showSuccess({ title: 'Posted! ', description: 'Your post was successfully created' });
        if (setIsLoading) {
          setIsLoading(false);
        }
      } catch (error: any) {
        showError({ title: 'Error', description: error.message });
        if (setIsLoading) {
          setIsLoading(false);
        }
      }
    },
  });

  const typeToText = (type: PostType) => {
    switch (type) {
      case PostType.Repost:
        return 'is re-posting';
      case PostType.Optin:
        return 'opted in for';
      default:
        /* istanbul ignore next */
        return '';
    }
  };

  // TODO: Improve this, modify the provider in order to not have to put this in the component
  useEffect(() => {
    return () => {
      hideNotification();
    };
  }, []);

  return (
    <div>
      <div className="flex items-center">
        <div className="w-[65px] mr-2 sm:mr-0">
          <Avatar image={user?.photoUrl || DEFAULT_USER_AVATAR} size="xs" className="w-[48px] h-[48px] rounded-xl" />
        </div>
        <div>
          🖐️ <span className="ml-1 font-medium text-lg">{user?.name}</span>&nbsp;
          <span className="font-medium text-lg text-gray-400">{typeToText(type)}</span>&nbsp;
          <span className="font-medium text-lg" data-testid="projectName">
            {projectName}
          </span>
        </div>
      </div>
      <div className="flex mt-3 sm:mt-2">
        <div className="sm:w-[75px]"></div>
        <div className="w-full">
          <FormikProvider value={form}>
            <TextArea
              id="note"
              name="note"
              autoComplete="off"
              placeholder={
                PostType.Repost
                  ? 'Share with the community something about the project (optional)'
                  : 'Why did you express interest? (optional)'
              }
              required
              rows={3}
              className="w-full sm:w-[482px]"
            />
          </FormikProvider>
        </div>
      </div>
      <div className="flex mt-5 items-center">
        <div className="sm:w-[75px]"></div>
        <div className="flex w-full items-center">
          <div className="flex-1">
            <div className="text-[#868686] px-4 py-2 text-sm font-normal bg-gray-200 border--gray-200 rounded-md leading-snug border border-gray-200 inline-flex items-center">
              {privacy === PostPrivacy.Public ? (
                'Public'
              ) : (
                <>
                  <Image src="/images/icons/lock.svg" width={16} height={18} />{' '}
                  <span className="ml-2">Only visible to you</span>
                </>
              )}
            </div>
            {canChangePrivacy && (
              <Button
                size="sm"
                color="white"
                className="ml-2 border-0 font-bold"
                onClick={() => {
                  if (privacy === PostPrivacy.Public) {
                    setPostPrivacy(PostPrivacy.Private);
                  } else {
                    setPostPrivacy(PostPrivacy.Public);
                  }
                }}
              >
                Make {privacy === PostPrivacy.Private ? 'public' : 'private'}
              </Button>
            )}
          </div>
          <div>
            <Button size="lg" onClick={() => form.submitForm()} name="post">
              Post
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPost;
